import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { WindowType, Category } from '@/features/product-configurator/types';
import { MOCK_PRODUCTS } from '@/features/product-configurator/api/products.mock';
import { MOCK_CATEGORIES } from '@/features/product-configurator/api/categories.mock';
import { ChatbotQuestion } from '@/features/product-configurator/services/chatbotConfigGenerator';

interface PricingRule {
  id: string;
  name: string;
  condition: string;
  amount: number;
  type: string;
  description: string;
}

interface ValidationRule {
  id: string;
  name: string;
  rule: string;
  message: string;
  severity: string;
}

interface ProductConfigContextType {
  products: WindowType[];
  setProducts: (products: WindowType[]) => void;
  categories: Category[];
  setCategories: (categories: Category[]) => void;
  pricingRules: PricingRule[];
  setPricingRules: (rules: PricingRule[]) => void;
  validationRules: ValidationRule[];
  setValidationRules: (rules: ValidationRule[]) => void;
  editedQuestions: ChatbotQuestion[];
  setEditedQuestions: (questions: ChatbotQuestion[]) => void;
  productConfigurations: Record<string, any>;
  setProductConfiguration: (productId: string, config: any) => void;
}

const ProductConfigContext = createContext<ProductConfigContextType | undefined>(undefined);

const STORAGE_KEY = 'victorian-sliders-product-config';

const DEFAULT_PRICING_RULES: PricingRule[] = [
  // Window type base prices
  { id: '1', name: 'Standard Window Base', condition: 'window_type = standard', amount: 299, type: 'Base', description: 'Base price for standard window' },
  { id: '2', name: 'Arch Window Base', condition: 'window_type = arch', amount: 629, type: 'Base', description: 'Base price for arch window (includes arch surcharge)' },
  { id: '3', name: 'Coupled 2 Windows Base', condition: 'window_type = coupled_2', amount: 620, type: 'Base', description: 'Base price for 2 coupled windows (includes 1 pair 6mm clips)' },
  { id: '4', name: 'Coupled 3 Windows Base', condition: 'window_type = coupled_3', amount: 941, type: 'Base', description: 'Base price for 3 coupled windows (includes 2 pairs 6mm clips)' },
  { id: '5', name: 'Coupled 4 Windows Base', condition: 'window_type = coupled_4', amount: 1262, type: 'Base', description: 'Base price for 4 coupled windows (includes 3 pairs 6mm clips)' },

  // Style modifiers
  { id: '6', name: 'Deep Bottom Rail Style', condition: 'style = deep_bottom_rail', amount: 35, type: 'Fixed', description: 'Add £35 for deep bottom rail style' },

  // Horns pricing (per window type)
  { id: '7', name: 'Horns - Standard/DBR/Arch', condition: 'horns AND (standard OR dbr OR arch)', amount: 28, type: 'Fixed', description: 'Add horns +£28' },
  { id: '8', name: 'Horns - Coupled 2', condition: 'horns AND coupled_2', amount: 48, type: 'Fixed', description: 'Add horns +£48' },
  { id: '9', name: 'Horns - Coupled 3', condition: 'horns AND coupled_3', amount: 72, type: 'Fixed', description: 'Add horns +£72' },
  { id: '10', name: 'Horns - Coupled 4', condition: 'horns AND coupled_4', amount: 96, type: 'Fixed', description: 'Add horns +£96' },
  { id: '11', name: 'Horns - Bay 3 Windows', condition: 'horns AND bay_3', amount: 96, type: 'Fixed', description: 'Add horns +£96 for bay with 3 windows' },

  // Profile Colours
  { id: '12', name: 'Standard Foils', condition: 'foil_external_only OR foil_both_same', amount: 70, type: 'Fixed', description: 'Standard foils +£70' },
  { id: '13', name: 'Special Foils', condition: 'special_foils', amount: 0, type: 'Matrix', description: 'Special foils - Basket pricing from external matrix' },
  { id: '14', name: 'Spray - Smooth PVC-U', condition: 'spray_substrate = smooth_pvcu', amount: 135, type: 'Base', description: 'Base spray cost on smooth PVC-U' },
  { id: '15', name: 'Spray - Woodgrain Both', condition: 'spray_substrate = woodgrain_both', amount: 205, type: 'Base', description: 'Base spray cost on woodgrain both sides' },
  { id: '16', name: 'Spray - Woodgrain Ext + Int Foil', condition: 'spray_substrate = woodgrain_ext_foil_int', amount: 205, type: 'Base', description: 'Base spray cost on woodgrain external with internal foil' },
  { id: '17', name: 'Spray Coverage Multiplier - Both', condition: 'spray_coverage = both', amount: 1.5, type: 'Multiplier', description: '1.5x multiplier for inside and outside spray' },

  // Cills
  { id: '18', name: 'Cill 185mm White', condition: 'cill_185 AND white', amount: 8, type: 'Fixed', description: '+£8 for 185mm white smooth' },
  { id: '19', name: 'Cill 185mm Coloured', condition: 'cill_185 AND coloured', amount: 13, type: 'Fixed', description: '+£13 for 185mm coloured' },
  { id: '20', name: 'Cill 225mm White', condition: 'cill_225 AND white', amount: 11, type: 'Fixed', description: '+£11 for 225mm white smooth' },
  { id: '21', name: 'Cill 225mm Coloured', condition: 'cill_225 AND coloured', amount: 17, type: 'Fixed', description: '+£17 for 225mm coloured' },
  { id: '22', name: 'Bay Welded Cill Returns', condition: 'bay AND per_weld', amount: 22, type: 'PerUnit', description: '£22 per weld - Auto-calculated from bay pole count' },

  // Hardware
  { id: '23', name: 'Hardware - Standard Tier 1', condition: 'hardware_color IN [gold, black, chrome, satin]', amount: 17, type: 'Fixed', description: '+£17 for standard hardware colours' },
  { id: '24', name: 'Hardware - Standard Tier 2', condition: 'hardware_color IN [antique_gold, rose_gold, black_nickel]', amount: 22, type: 'Fixed', description: '+£22 for premium hardware colours' },
  { id: '25', name: 'D-Handles - Standard', condition: 'dhandles_standard', amount: 6, type: 'Fixed', description: '+£6 for standard D-handles' },
  { id: '26', name: 'D-Handles - Premium', condition: 'dhandles_premium', amount: 9, type: 'Fixed', description: '+£9 for premium D-handles' },
  { id: '27', name: 'Finger Lifts', condition: 'finger_lifts', amount: 10, type: 'Fixed', description: '+£10 for inset finger lifts' },

  // Security
  { id: '28', name: 'Upgraded Security Lock', condition: 'security = upgraded', amount: 18, type: 'Fixed', description: '+£18 for upgraded security lock' },
  { id: '29', name: 'PAS 24 / Document Q', condition: 'security = pas24', amount: 85, type: 'Fixed', description: '+£85 for PAS 24 compliance' },
  { id: '30', name: 'Secured by Design', condition: 'security = sbd', amount: 115, type: 'Fixed', description: '+£115 for Secured by Design' },

  // Decorative Beads
  { id: '31', name: 'Decorative Beads', condition: 'bars != none', amount: 0, type: 'Matrix', description: 'Matrix pricing from external grid' },

  // Trickle Vents
  { id: '32', name: 'Trickle Vents', condition: 'trickle_vents_enabled', amount: 14, type: 'PerWindow', description: '£14 per window with trickle vents' },

  // Glazing
  { id: '33', name: '1.2 U-Value Glass', condition: 'glass_u12', amount: 17, type: 'Fixed', description: '+£17 for 1.2 U-Value glass' },
  { id: '34', name: 'Laminated/Acoustic', condition: 'glass_laminated', amount: 0, type: 'TBC', description: 'Laminated/Acoustic glass - Price TBC' },

  // Spacer Bar
  { id: '35', name: 'White Spacer Bar', condition: 'spacer_white', amount: 11, type: 'PerWindow', description: '+£11 per window for white spacer bar' },

  // Optional Extras
  { id: '36', name: 'Weather Bar', condition: 'weather_bar', amount: 15, type: 'Fixed', description: '+£15 for weather bar' },
];

const DEFAULT_VALIDATION_RULES: ValidationRule[] = [
  // Dimensional constraints (from business knowledge, not in latest spec)
  { id: '1', name: 'Minimum Width', rule: 'width >= 370mm', message: 'Minimum width is 370mm', severity: 'Blocking Error' },
  { id: '2', name: 'Maximum Width', rule: 'width <= 1600mm', message: 'Maximum width for a single frame is 1600mm', severity: 'Blocking Error' },
  { id: '3', name: 'Minimum Height', rule: 'height >= 740mm', message: 'Minimum height is 740mm', severity: 'Blocking Error' },
  { id: '4', name: 'Maximum Height', rule: 'height <= 3000mm', message: 'Maximum height for a single frame is 3000mm', severity: 'Blocking Error' },

  // Special Foils validation (per spec)
  { id: '5', name: 'Special Foils Color Match', rule: 'internal_color !== external_color', message: 'Internal and external colours cannot be the same for special foils', severity: 'Blocking Error' },

  // Arch-specific validations
  { id: '6', name: 'Arch Requires Radius', rule: 'IF window_type = arch THEN radius IS REQUIRED', message: 'Arch windows require both height AND radius fields', severity: 'Blocking Error' },
  { id: '7', name: 'Arch No Trickle Vents', rule: 'IF window_type = arch THEN trickle_vents = false', message: 'Arch windows cannot have trickle vents', severity: 'Blocking Error' },

  // Profile Colours mutual exclusion (per spec)
  { id: '8', name: 'Profile Colour Mutual Exclusion', rule: 'Only one profile colour group selected', message: 'Once any profile colour is selected, gray out other groups until you hit Clear', severity: 'Warning' },

  // Coupled constraints
  { id: '9', name: 'Coupled Max Width', rule: 'total_width <= 4000mm', message: 'Maximum overall width for coupled units is 4000mm', severity: 'Blocking Error' },

  // Glass mandatory (per spec)
  { id: '10', name: 'Glass Mandatory', rule: 'glass_type != null', message: 'Glass selection is mandatory', severity: 'Blocking Error' },

  // Sash Function restored (per spec)
  { id: '11', name: 'Sash Function Required', rule: 'sash_function != null', message: 'Sash function (Fixed or Sliding) must be selected', severity: 'Blocking Error' },

  // Bay-specific validations
  { id: '12', name: 'Bay Requires Per-Window Widths', rule: 'IF window_type = bay THEN all window widths REQUIRED', message: 'Bay windows require width for each individual window', severity: 'Blocking Error' },
  { id: '13', name: 'Bay Structural Posts Box Bay Only', rule: 'IF structural_bay_posts THEN bay_type = BoxBay', message: 'Structural Bay Posts are only available for Box Bay configurations', severity: 'Blocking Error' },
  { id: '14', name: 'Bay Jacks Box Bay Only', rule: 'IF bay_jacks THEN bay_type = BoxBay', message: 'Bay Jacks are only available for Box Bay configurations', severity: 'Blocking Error' },

  // Trickle vents default (per spec)
  { id: '15', name: 'Trickle Vents Default Enabled', rule: 'trickle_vents_enabled = true BY DEFAULT', message: 'Trickle vents are enabled by default - must be explicitly disabled', severity: 'Warning' },

  // Hardware matching
  { id: '16', name: 'Finger Lifts Match Hardware', rule: 'IF finger_lifts THEN colour MATCHES hardware_colour', message: 'Inset Finger Lifts must match selected hardware colour', severity: 'Blocking Error' },

  // Spray colour requirements
  { id: '17', name: 'Spray Requires Colour Selection', rule: 'IF spray_substrate THEN spray_coverage AND spray_colour REQUIRED', message: 'Spray finish requires coverage selection and colour specification', severity: 'Blocking Error' },
];

export const ProductConfigProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [products, setProductsState] = useState<WindowType[]>(() => {
    const saved = localStorage.getItem(`${STORAGE_KEY}-products`);
    return saved ? JSON.parse(saved) : MOCK_PRODUCTS;
  });

  const [categories, setCategoriesState] = useState<Category[]>(() => {
    const saved = localStorage.getItem(`${STORAGE_KEY}-categories`);
    return saved ? JSON.parse(saved) : MOCK_CATEGORIES;
  });

  const [pricingRules, setPricingRulesState] = useState<PricingRule[]>(() => {
    const saved = localStorage.getItem(`${STORAGE_KEY}-pricing-rules`);
    return saved ? JSON.parse(saved) : DEFAULT_PRICING_RULES;
  });

  const [validationRules, setValidationRulesState] = useState<ValidationRule[]>(() => {
    const saved = localStorage.getItem(`${STORAGE_KEY}-validation-rules`);
    return saved ? JSON.parse(saved) : DEFAULT_VALIDATION_RULES;
  });

  const [editedQuestions, setEditedQuestionsState] = useState<ChatbotQuestion[]>(() => {
    const saved = localStorage.getItem(`${STORAGE_KEY}-edited-questions`);
    return saved ? JSON.parse(saved) : [];
  });

  const [productConfigurations, setProductConfigurationsState] = useState<Record<string, any>>(() => {
    const saved = localStorage.getItem(`${STORAGE_KEY}-product-configs`);
    return saved ? JSON.parse(saved) : {};
  });

  useEffect(() => {
    localStorage.setItem(`${STORAGE_KEY}-products`, JSON.stringify(products));
  }, [products]);

  useEffect(() => {
    localStorage.setItem(`${STORAGE_KEY}-categories`, JSON.stringify(categories));
  }, [categories]);

  useEffect(() => {
    localStorage.setItem(`${STORAGE_KEY}-pricing-rules`, JSON.stringify(pricingRules));
  }, [pricingRules]);

  useEffect(() => {
    localStorage.setItem(`${STORAGE_KEY}-validation-rules`, JSON.stringify(validationRules));
  }, [validationRules]);

  useEffect(() => {
    localStorage.setItem(`${STORAGE_KEY}-edited-questions`, JSON.stringify(editedQuestions));
  }, [editedQuestions]);

  useEffect(() => {
    localStorage.setItem(`${STORAGE_KEY}-product-configs`, JSON.stringify(productConfigurations));
  }, [productConfigurations]);

  const setProducts = (newProducts: WindowType[]) => {
    setProductsState(newProducts);
  };

  const setCategories = (newCategories: Category[]) => {
    setCategoriesState(newCategories);
  };

  const setPricingRules = (newRules: PricingRule[]) => {
    setPricingRulesState(newRules);
  };

  const setValidationRules = (newRules: ValidationRule[]) => {
    setValidationRulesState(newRules);
  };

  const setEditedQuestions = (newQuestions: ChatbotQuestion[]) => {
    setEditedQuestionsState(newQuestions);
  };

  const setProductConfiguration = (productId: string, config: any) => {
    setProductConfigurationsState(prev => ({
      ...prev,
      [productId]: config
    }));
  };

  return (
    <ProductConfigContext.Provider
      value={{
        products,
        setProducts,
        categories,
        setCategories,
        pricingRules,
        setPricingRules,
        validationRules,
        setValidationRules,
        editedQuestions,
        setEditedQuestions,
        productConfigurations,
        setProductConfiguration,
      }}
    >
      {children}
    </ProductConfigContext.Provider>
  );
};

export const useProductConfig = () => {
  const context = useContext(ProductConfigContext);
  if (!context) {
    throw new Error('useProductConfig must be used within ProductConfigProvider');
  }
  return context;
};
